// Prequalification server-side drafts — the ANONYMOUS surface (no session).
//
// Mirrors the four routes on PortalPrequalificationController. Two rules from
// that controller shape everything here and are worth restating, because
// breaking either one is silent from the client's side:
//
//   1. A draft is NEVER addressable by id. The token is the lookup, it rides
//      the query string, and EXACTLY ONE of `?invite=` or `?draft=` may be
//      present — never both, never neither. THE PARAMETER NAME SELECTS THE
//      STORE, so `withToken` below prefers the invite and never emits both.
//
//   2. The route's partner slug is what the server resolves the brand from,
//      and a draft may only be read or written under the brand it was created
//      under. Every call therefore goes through the same path builder: drop
//      the slug on one call and that call alone starts 404-ing.
//
// Failure vocabulary, once the token HAS resolved: 400 carries a renderable
// message, 409 means somebody else wrote to this draft, 404 means the token
// resolved to nothing (and is deliberately bodyless — it says nothing about
// whether the token was unknown, expired, revoked, or for another brand).

import { portalApi } from "./client";
import type { PrequalFinalizeResult, PrequalRole } from "./prequalification";
import type { PrequalAttribution } from "../pages/prequalify/prequalAttribution";

/** Base64 on the wire — a `byte[]` on the C# side. Echoed back UNMODIFIED on
 *  the next write; a missing or stale stamp is a 409, never last-write-wins. */
export type PrequalRowVersion = string;

/** The token pair. Exactly one member is ever populated. */
export interface PrequalDraftToken {
  draft?: string | null;
  invite?: string | null;
}

export interface PrequalDraftCreateBody {
  firstName: string;
  lastName: string;
  email: string;
  /** The role's answers object, serialized. Size-capped and schema-checked
   *  server-side — an unknown member is a 400, not a silent discard. */
  answersJson: string;
  stepIndex: number;
  /** Where the visit came from. Stored on the DRAFT so a resume on another
   *  device — where session storage is gone — still credits the campaign. */
  attribution?: PrequalAttribution;
}

export interface PrequalDraftCreateResponse {
  draftId: string | null;
  rowVersion: PrequalRowVersion | null;
  stepIndex: number;
  stepCount: number;
  /** ORGANIC path only, and the ONLY moment this token is ever emitted to
   *  anyone: there is no email fallback, so a caller that drops it strands the
   *  visitor permanently. A full URL rather than a bare token, so nobody
   *  assembles it with the wrong parameter name. */
  resumeUrl: string | null;
  /** True when an already-started invite re-opened its existing draft. */
  resumed: boolean;
  error: string | null;
}

export interface PrequalDraftResumeResponse {
  draftId: string;
  role: PrequalRole;
  firstName: string;
  lastName: string;
  email: string;
  partnerSlug: string | null;
  stepIndex: number;
  /** The denominator, from the server's own funnel-step constant. */
  stepCount: number;
  answersJson: string;
  rowVersion: PrequalRowVersion | null;
  lastActivityAtUtc: string;
}

export interface PrequalDraftStepBody {
  rowVersion: PrequalRowVersion | null;
  stepIndex: number;
  answersJson: string;
}

export interface PrequalDraftPersistResponse {
  draftId: string | null;
  rowVersion: PrequalRowVersion | null;
  stepIndex: number;
  error: string | null;
}

export interface PrequalDraftFinalizeBody {
  rowVersion: PrequalRowVersion | null;
  consentAccepted: boolean;
  answersJson: string;
  phone?: string;
  /** Honeypot — hidden on the real form; always empty for humans. */
  website?: string;
  /**
   * The `/buy` continuation token, still in this browser's URL as `?blt=`.
   *
   * 🔴 Required on THIS path too, not just the ordinary finalize. A visitor who
   * came from `/buy` and then saved progress — or merely stepped Back and
   * Continue, which mints a draft once contact details exist — submits through
   * here instead. Omitting it recreates the duplicate buyer lead the token
   * exists to prevent, silently.
   *
   * Unlike `attribution` there is no draft column behind this, so a draft
   * resumed in a NEW session (no `?blt=` in the URL) will not attach. Disclosed
   * limitation, not an oversight — see the server-side field docs.
   */
  buyLandingToken?: string;
  /**
   * The per-advertisement link token, still in this browser's URL as `?ad=`
   * (decision D2). Same treatment as `buyLandingToken` above, for the same
   * reason: this path is reached by an ordinary `/buy` visitor who pressed
   * Back, and there is no draft column behind it — a draft resumed in a new
   * session carries none and lands without advertisement credit.
   */
  advertisementToken?: string;
  /**
   * A late identity correction, sent only when the visitor actually changed who
   * the application is for after the draft was saved. The funnel asks for
   * contact details on its LAST step, so this is the ordinary way an email typo
   * gets noticed.
   *
   * Honoured by the server for an ORGANIC draft only. On an invited draft the
   * invite stays authoritative and these are ignored, so a recipient cannot
   * redirect a broker's outreach onto a different person while keeping that
   * broker's attribution.
   */
  /** Where the visit came from, as this browser still holds it. A fallback:
   *  the draft's own stored value wins server-side. */
  attribution?: PrequalAttribution;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/** `/prequalification[/sites/{slug}]/drafts/{tail}`. One builder for all four
 *  calls so the brand segment cannot be present on some and absent on others. */
function draftPath(slug: string | null | undefined, tail: string): string {
  return slug
    ? `/prequalification/sites/${encodeURIComponent(slug)}/drafts/${tail}`
    : `/prequalification/drafts/${tail}`;
}

/** Appends exactly one token parameter. The invite wins when both are somehow
 *  in hand: an invited draft has no resume token of its own, so `?draft=` could
 *  only be a stale value from the address bar, and sending both is a 404. */
function withToken(path: string, token: PrequalDraftToken): string {
  if (token.invite) return `${path}?invite=${encodeURIComponent(token.invite)}`;
  if (token.draft) return `${path}?draft=${encodeURIComponent(token.draft)}`;
  return path;
}

function rolePath(role: PrequalRole): string {
  return role === "Seller" ? "seller" : "buyer";
}

export const prequalDraftsApi = {
  /** Start a draft. With `?invite=` the server copies identity, brand and
   *  sender from the invite and IGNORES the body's name/email; an unresolvable
   *  invite is a 404 that writes nothing (never a fallback to organic). */
  create: (
    role: PrequalRole,
    slug: string | null,
    inviteToken: string | null,
    body: PrequalDraftCreateBody,
  ) =>
    portalApi.post<PrequalDraftCreateResponse>(
      withToken(draftPath(slug, `start/${rolePath(role)}`), { invite: inviteToken }),
      body,
    ),

  /** Resume. Reading is NOT activity — this does not bump last-activity, which
   *  is what keeps mail scanners and link previews from making every invited
   *  draft look permanently alive. */
  resume: (slug: string | null, token: PrequalDraftToken) =>
    portalApi.get<PrequalDraftResumeResponse>(withToken(draftPath(slug, "resume"), token)),

  /** Persist one step. The row version from the last read must come back with
   *  it; a mismatch is a 409. */
  step: (slug: string | null, token: PrequalDraftToken, body: PrequalDraftStepBody) =>
    portalApi.put<PrequalDraftPersistResponse>(withToken(draftPath(slug, "step"), token), body),

  /** Finalize: submission insert, documents, and the draft's deletion, in one
   *  transaction. Identity comes from the DRAFT row, except that an ORGANIC
   *  draft accepts a late correction in the body — see the field docs above. */
  finalize: (slug: string | null, token: PrequalDraftToken, body: PrequalDraftFinalizeBody) =>
    portalApi.post<PrequalFinalizeResult>(withToken(draftPath(slug, "finalize"), token), body),
};
